import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, L, pickLang } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { StoryCard } from "@/components/news/StoryCard";
import { Skeleton } from "@/components/ui/skeleton";

const Ticker = ({ items, lang }) => {
  if (!items.length) return null;
  const loop = [...items, ...items];
  return (
    <div data-testid="breaking-ticker" className="bg-[#0B2A5B] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto flex items-stretch">
        <span className="shrink-0 bg-[#1D4ED8] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] flex items-center">
          {L("breaking", lang)}
        </span>
        <div className="overflow-hidden flex-1">
          <div className={`ticker flex whitespace-nowrap py-2 w-max ${lang === "ta" ? "lang-ta" : ""}`}>
            {loop.map((s, i) => (
              <Link key={i} to={`/story/${s.slug}`} className="px-6 text-sm font-semibold hover:underline">
                {s[pickLang(s, lang)]?.headline}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHead = ({ trend, lang, color }) => (
  <div className="flex items-end justify-between border-b-2 border-[#0B2A5B] pb-2 mb-8">
    <h2 className={`font-serif-head text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-3 ${lang === "ta" ? "lang-ta" : ""}`}>
      <span className="h-3 w-3 inline-block" style={{ background: color }} />
      {L(trend, lang)}
    </h2>
    <Link data-testid={`section-more-${trend}`} to={`/trend/${trend}`} className="text-xs font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1 hover:text-[#1D4ED8]">
      {lang === "ta" ? "அனைத்தும்" : "View all"} <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </div>
);

const Grid = ({ items, lang }) => (
  <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((s) => <StoryCard key={s.id} story={s} lang={lang} />)}
  </div>
);

export default function Home() {
  const { lang } = useSite();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/home").then((r) => setData(r.data)).catch(() => setData({ breaking: [], hot: [], regular: [] }));
  }, []);

  if (!data)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 lg:grid-cols-12">
        <Skeleton className="lg:col-span-8 h-[420px]" />
        <Skeleton className="lg:col-span-4 h-[420px]" />
      </div>
    );

  const lead = data.breaking[0] || data.hot[0] || data.regular[0];
  const side = [...data.breaking, ...data.hot, ...data.regular].filter((s) => s.id !== lead?.id).slice(0, 5);
  const empty = !lead;

  return (
    <div data-testid="home-page" className="fade-in">
      <Ticker items={data.breaking} lang={lang} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {empty ? (
          <p data-testid="home-empty" className="py-24 text-zinc-500">{L("empty", lang)}</p>
        ) : (
          <section data-testid="home-lead" className="grid lg:grid-cols-12 border-b border-zinc-300 py-10 gap-y-10">
            <div className="lg:col-span-8 lg:pr-10 lg:border-r border-zinc-300">
              <StoryCard story={lead} lang={lang} variant="lead" />
            </div>
            <div className="lg:col-span-4 lg:pl-10">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">
                {lang === "ta" ? "தற்போது" : "Right now"}
              </p>
              <ol className="divide-y divide-zinc-200">
                {side.map((s, i) => (
                  <li key={s.id} className="py-4 flex gap-4">
                    <span className="font-serif-head text-3xl font-black text-zinc-300 leading-none w-6">{i + 1}</span>
                    <div className="flex-1"><StoryCard story={s} lang={lang} variant="compact" /></div>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {data.breaking.length > 0 && (
          <section data-testid="section-breaking" className="pt-16">
            <SectionHead trend="breaking" lang={lang} color="#DC2626" />
            <Grid items={data.breaking} lang={lang} />
          </section>
        )}
        {data.hot.length > 0 && (
          <section data-testid="section-hot" className="pt-16">
            <SectionHead trend="hot" lang={lang} color="#EA580C" />
            <Grid items={data.hot} lang={lang} />
          </section>
        )}
        {data.regular.length > 0 && (
          <section data-testid="section-regular" className="pt-16">
            <SectionHead trend="regular" lang={lang} color="#0B2A5B" />
            <Grid items={data.regular} lang={lang} />
          </section>
        )}
      </div>
    </div>
  );
}
